import { PaginatedResource } from '../../typings/Paginated';
import { BaseInscription, ExtraFiles, Inscription, InscriptionsPaginatedParameters, SimplifiedCloudProvider } from '../../typings/third-party-approved-companies';
import { AlreadyExists, BaseService, ClientError, ExtendableError, InvalidValue, Unauthorized, UnexpectedError } from './base-service';
import { GetDonatedCamerasPaginatedParameters } from './cameras';

export const inscriptionStatus = [
    'pending',
    'waiting',
    'irregular',
    'approved',
    'failed',
    'published',
] as const;
export type InscriptionStatus = typeof inscriptionStatus[number];

export const companyType = [
    'cloud',
    'images',
    'both'
] as const;
export type CompanyType = typeof companyType[number];
export const companyTypeWithoutBoth = ['cloud', 'images'] as const;
export type CompanyTypeWithoutBoth = typeof companyTypeWithoutBoth[number];

export enum DocumentType {
    'CPF' = 'cpf',
    'RG' = 'rg',
}

export enum ResponseTime {
    'INSTANTLY' = 'instantly',
    'FAST' = 'fast',
    'NORMAL' = 'normal',
    'SLOW' = 'slow',
    'VERY_SLOW' = 'very_slow',
    'DO_NOT_LOAD' = 'do_not_load'
}

export enum ImageQuality {
    'EXCELLENT' = 'excellent',
    'GOOD' = 'good',
    'NORMAL' = 'normal',
    'BAD' = 'bad',
    'TERRIBLE' = 'terrible'
}

export enum Framerate {
    'FLUID' = 'fluid',
    'GOOD' = 'good',
    'LOCKED' = 'locked'
}

export enum TimelineConsistency {
    'CONSTANT' = 'constant',
    'CHOPPED' = 'chopped',
    'VERY_CHOPPED' = 'very_chopped',
    'EMPTY' = 'empty'
}

export enum CameraGeolocation {
    'CORRECT' = 'correct',
    'CLOSE_ENOUGH' = 'close_enough',
    'INCORRECT' = 'incorrect',
}

export class IsUsedAsCloudProvider extends ExtendableError { }

export class ThirdPartyApprovedCompaniesService extends BaseService {
    async getInscriptions(params: InscriptionsPaginatedParameters): Promise<PaginatedResource<BaseInscription>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/approved-companies${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createInscription(params: FormData): Promise<Inscription> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/approved-company`, {
            method: 'POST',
            body: params,
            headers: this.getHeaders(token)
        });

        if (res.status != 201) {
            if (res.status === 400) {
                const resJson = await res.json();
                if (resJson.code === 'AlreadyExists') {
                    throw new AlreadyExists();
                } else if (resJson.code === 'InvalidValue') {
                    throw new InvalidValue();
                }
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }

        return res.json();
    }

    async getInscription(id: string): Promise<Inscription> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/approved-company/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async addExtraFileForInscription(id: string, file: File): Promise<ExtraFiles> {
        const token = await this.getToken();
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${this.centralEndpoint}/third-party/approved-company/${id}/extra-file`, {
            method: 'PUT',
            body: formData,
            headers: this.getHeaders(token)
        });

        if (res.status != 200) {
            if (res.status === 400) {
                const resJson = await res.json();
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }

        return res.json();
    }

    async deleteExtraFileForInscription(inscriptionId: string, fileId: string): Promise<ExtraFiles> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/approved-company/${inscriptionId}/extra-file/${fileId}`, {
            method: 'DELETE',
            headers: this.getHeaders(token)
        });

        if (res.status != 200) {
            if (res.status === 400) {
                const resJson = await res.json();
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }
        return res.json();
    }

    async updateInscription(id: string, params: FormData): Promise<Inscription> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/approved-company/${id}`, {
            method: 'PUT',
            body: params,
            headers: this.getHeaders(token)
        });

        if (res.status != 200) {
            if (res.status === 400) {
                const resJson = await res.json();
                if (resJson.code === 'AlreadyExists') {
                    throw new AlreadyExists();
                } else if (resJson.code === 'InvalidValue') {
                    throw new InvalidValue();
                }
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }

        return res.json();
    }

    async deleteInscriptions(ids: string[]): Promise<Inscription[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/approved-companies`, {
            method: 'DELETE',
            body: JSON.stringify({
                ids: ids
            }),
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status != 200) {
            if (res.status === 400) {
                const resJson = await res.json();
                if (resJson.code === 'IsUsedAsCloudProvider') {
                    throw new IsUsedAsCloudProvider();
                }
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }

        return res.json();
    }

    async getMultipleInscription(params: { ids: string[]; }): Promise<Inscription[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/multiple-approved-companies`, {
            method: 'POST',
            body: JSON.stringify(params),
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateMultipleInscriptions(params: { ids: string[]; status: InscriptionStatus; }): Promise<Inscription[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/multiple-approved-companies`, {
            method: 'PUT',
            body: JSON.stringify(params),
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedPublishedCloudProviders(): Promise<SimplifiedCloudProvider[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/approved-companies-cloud-providers`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDonatedCamerasPaginated(params: GetDonatedCamerasPaginatedParameters): Promise<PaginatedResource<PaginatedDonatedCamera>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/third-party/donated-cameras/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteDonatedCamera(id: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/third-party/donated-cameras/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async reEnableDonatedCamera(id: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/third-party/donated-cameras/${id}/re-enable`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }
}

export const thirdPartyApprovedCompaniesService = new ThirdPartyApprovedCompaniesService();
